import {
  createStyles,
  withStyles,
  Button,
  DeadIcon,
  Grid,
  ThemeProvider,
  Typography,
  createTheme,
  Link
} from '@paintscout/ui'
import type { MuiTheme as Theme, WithStyles } from '@paintscout/ui'
import * as Sentry from '@sentry/react'
import type { UnregisterCallback } from 'history'
import React from 'react'
import type { RouteComponentProps } from 'react-router-dom'
import { withRouter } from 'react-router-dom'
import { escapeHref } from '@paintscout/util'

const theme = createTheme({
  palette: {
    primary: { main: '#595959' },
    secondary: { main: '#BFBEBE' },
    error: { main: '#F44336' }
  }
})

const styles = (theme: Theme) => {
  return createStyles({
    wrapper: {
      height: (props: ErrorBoundaryProps) =>
        `calc(100vh - ${props.isRootBoundary ? '0px' : theme.paintscout.header.height.md})`,
      width: '100vw',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      background: '#fff',
      position: 'relative'
    },
    root: {
      margin: '0',
      color: '#595959',
      '& a': {
        color: '#595959'
      },
      '& a:hover': {
        textDecoration: 'none'
      },
      textAlign: 'center'
    },
    deadIcon: {
      width: theme.spacing(10),
      height: theme.spacing(10),
      color: '#595959',
      [theme.breakpoints.down('sm')]: {
        width: theme.spacing(7),
        height: theme.spacing(7)
      }
    },

    logoutLink: {
      color: 'rgba(0, 0, 0, 0.54) !important'
    },
    error: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      width: '100%'
    },
    errorMessage: {
      background: 'rgba(0, 0, 0, 0.05)',
      color: theme.palette.primary.main,
      whiteSpace: 'pre',
      padding: '0 20px',
      overflow: 'auto',
      transition: 'max-height 300ms'
    }
  })
}

interface ErrorBoundaryState {
  error?: string
  info?: {
    componentStack: string
  }
  errorExpanded: boolean
}

export interface ErrorBoundaryProps {
  isRootBoundary?: boolean
  title?: string
  children?: React.ReactNode
}

export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps & WithStyles<typeof styles> & RouteComponentProps,
  ErrorBoundaryState
> {
  public historyListener: UnregisterCallback = null

  state: ErrorBoundaryState = { error: null, info: null, errorExpanded: false }

  /**
   * Dev mode only - clears error page when hitting ESC key
   */
  public onKeyDown = (event: any) => {
    if (event.keyCode === 27) {
      window.ERROR_CAUGHT = null
      this.setState({ error: null, info: null })
    }
  }

  public componentDidMount() {
    if (process.env.NODE_ENV === 'development') {
      document.addEventListener('keydown', this.onKeyDown)
    }

    this.historyListener = this.props.history.listen(() => {
      window.ERROR_CAUGHT = null
      this.setState({ error: null, info: null })
    })
  }

  public componentWillUnmount() {
    if (process.env.NODE_ENV === 'development') {
      document.removeEventListener('keydown', this.onKeyDown)
    }

    this.historyListener()
  }

  public componentDidCatch(error: any, info: any) {
    console.log(error)
    Sentry.captureException(error, {
      tags: {
        crash: 'app'
      }
    })

    window.ERROR_CAUGHT = error
    this.setState({ error, info })
  }

  public render() {
    const { error } = this.state
    const { classes, title, isRootBoundary, children } = this.props

    if (error) {
      const content = (
        <div className={classes.wrapper}>
          <div className={classes.root}>
            <Grid container spacing={3} justifyContent="center" direction="column">
              <Grid item>
                <Typography variant="h1">{title ? title : 'Something went wrong'}</Typography>
              </Grid>
              <Grid item>
                <DeadIcon className={classes.deadIcon} />
              </Grid>
              <Grid item>
                <Typography variant="h3">
                  Please <a href="mailto:support@paintscout.com">contact support</a> or try one of the options below
                </Typography>
              </Grid>
              <Grid item>
                <Grid container justifyContent="center" spacing={3}>
                  <Grid item>
                    <Button onClick={this.reloadPage}>Reload Page</Button>
                  </Grid>
                  <Grid item>
                    {isRootBoundary && (
                      <Link to="/">
                        <Button
                          onClick={() => {
                            this.setState({ error: null, info: null })
                          }}
                        >
                          Go to Home Page
                        </Button>
                      </Link>
                    )}
                    {!isRootBoundary && (
                      <Button
                        onClick={() => {
                          this.setState({ error: null, info: null })
                          this.props.history.goBack()
                        }}
                      >
                        Go Back
                      </Button>
                    )}
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Grid container spacing={1} direction="column">
                  <Grid item>
                    <Typography>
                      <Link
                        className={classes.logoutLink}
                        to="/logout"
                        onClick={() => {
                          this.setState({ error: null, info: null })
                        }}
                      >
                        If you&lsquo;re stuck on this page, click here to log out.
                      </Link>
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography>
                      <a href="mailto:support@paintscout.com">support@paintscout.com</a>
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </div>
        </div>
      )
      return isRootBoundary ? <ThemeProvider theme={theme}>{content}</ThemeProvider> : content
    }

    return children
  }

  private reloadPage() {
    const currentPath = window.location.pathname
    if (currentPath.startsWith('/reports/')) {
      // Reload the page without query parameters
      const target = window.location.origin + currentPath
      window.location.href = escapeHref(target)
    } else {
      // Reload the page normally
      window.location.reload()
    }
  }
}

export default withStyles(styles)(withRouter(ErrorBoundary))
